import { useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api';
import {
  ABILITIES,
  ABILITY_LABELS,
  type Ability,
  COLUMN_LABELS,
  SECTION_LABELS,
  SECTIONS,
} from '../../lib/permissions';
import { isAdmin } from '../../lib/roles';
import {
  type Density,
  type GroupRole,
  type MemberLayout,
  type RosterMember,
  type Section,
  TASK_STATUSES,
} from '../../lib/types';
import { useStore } from '../../store/useStore';
import { ErrorLine, Section as Block } from './ui';

/**
 * Who is on the team, and what the team is for them.
 *
 * Every other settings tab configures the project; this one configures a person.
 * The three things it decides are deliberately kept apart, because they fail in
 * different ways: the role is who may administer, the permissions are what they
 * may see and do, and the layout is only where they start — which is why the
 * layout is the one an admin can hand over and the member can then change.
 */
export function TeamTab() {
  const groupId = useStore((s) => s.activeGroupId);
  const fields = useStore((s) => s.fields);
  const user = useStore((s) => s.user);
  const refresh = useStore((s) => s.refresh);

  const [members, setMembers] = useState<RosterMember[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The roster is read from its own endpoint rather than from the sync delta:
  // permissions and layouts are an admin's view of the team, and the delta
  // carries only the plain member list every member is allowed to see.
  const reload = useCallback(async () => {
    if (!groupId) return;
    const result = await api<{ members: RosterMember[] }>(`/api/groups/${groupId}/roster`);
    setMembers(result.members);
  }, [groupId]);

  useEffect(() => {
    void reload().catch(() => undefined);
  }, [reload]);

  /**
   * Every change is a patch against one member, and every one of them refreshes
   * both this roster and the board's own copy — a field reassignment changes what
   * the admin making it can see as well.
   */
  async function patch(
    userId: string,
    body: Record<string, unknown>,
    whenItFails: string,
  ): Promise<void> {
    setError(null);
    try {
      await api(`/api/groups/${groupId}/members/${userId}`, { method: 'PATCH', body });
      await reload();
      await refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : whenItFails);
    }
  }

  async function remove(userId: string): Promise<void> {
    setError(null);
    try {
      await api(`/api/groups/${groupId}/members/${userId}`, { method: 'DELETE' });
      await reload();
      await refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not remove that member');
    }
  }

  return (
    <Block
      title="Team members"
      description="One place per person: the role they hold, the dev fields they work in, what they may see and do, and the view they start on."
    >
      <div className="flex flex-col gap-2">
        {members.map((member) => (
          <MemberRow
            key={member.id}
            member={member}
            fields={fields}
            isSelf={member.id === user?.id}
            open={openId === member.id}
            onToggle={() => setOpenId(openId === member.id ? null : member.id)}
            onPatch={(body, whenItFails) => patch(member.id, body, whenItFails)}
            onRemove={() => remove(member.id)}
          />
        ))}
      </div>

      {members.length === 0 && (
        <p className="card p-6 text-center text-sm text-ink-faint">
          Nobody here yet. Hand out the join code or an invite from the Joining tab.
        </p>
      )}

      <ErrorLine message={error} />
    </Block>
  );
}

type FieldSummary = { id: string; name: string; color: string };

function MemberRow({
  member,
  fields,
  isSelf,
  open,
  onToggle,
  onPatch,
  onRemove,
}: {
  member: RosterMember;
  fields: FieldSummary[];
  isSelf: boolean;
  open: boolean;
  onToggle: () => void;
  onPatch: (body: Record<string, unknown>, whenItFails: string) => Promise<void>;
  onRemove: () => void;
}) {
  const admin = isAdmin(member.role);
  const theirFields = fields.filter((field) => member.fieldIds.includes(field.id));

  return (
    <div className="card p-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">
            {member.displayName}
            {isSelf && <span className="text-ink-faint"> · you</span>}
          </p>
          <p className="truncate text-xs text-ink-faint">{member.email}</p>
        </div>

        <span className="flex min-w-0 flex-wrap items-center gap-1.5">
          {admin ? (
            <span className="text-xs text-ink-faint">every field</span>
          ) : theirFields.length > 0 ? (
            theirFields.map((field) => (
              <span
                key={field.id}
                className="flex items-center gap-1.5 rounded-full border border-line px-2 py-0.5 text-[11px] text-ink-soft"
              >
                <span
                  className="size-1.5 rounded-full"
                  style={{ backgroundColor: field.color }}
                />
                {field.name}
              </span>
            ))
          ) : (
            <span className="text-xs text-blocked">no field — sees nothing</span>
          )}
        </span>

        <span className="ml-auto flex shrink-0 items-center gap-2">
          <select
            value={member.role}
            onChange={(event) =>
              void onPatch({ role: event.target.value as GroupRole }, 'Could not change that role')
            }
            className="field-input !py-1 text-xs"
            aria-label={`Role for ${member.displayName}`}
          >
            <option value="member">Member</option>
            <option value="admin">Admin</option>
            <option value="owner">Owner</option>
          </select>
          <button type="button" onClick={onToggle} className="btn btn-ghost !py-1 !text-xs">
            {open ? 'Done' : 'Configure'}
          </button>
          {!isSelf && (
            <button type="button" onClick={onRemove} className="btn btn-danger !py-1 !text-xs">
              Remove
            </button>
          )}
        </span>
      </div>

      {open && (
        <div className="animate-rise mt-3 flex flex-col gap-5 border-t border-line pt-4">
          {admin ? (
            <p className="text-xs leading-relaxed text-ink-faint">
              Admins and owners see every field and every page, and can administer the team. There
              is nothing to restrict here — make them a member first to give them a narrower seat.
            </p>
          ) : (
            <>
              <FieldAccess member={member} fields={fields} onPatch={onPatch} />
              <SectionAccess member={member} onPatch={onPatch} />
              <AbilityAccess member={member} onPatch={onPatch} />
            </>
          )}

          <StartingLayout member={member} fields={fields} onPatch={onPatch} />
        </div>
      )}
    </div>
  );
}

type PatchFn = (body: Record<string, unknown>, whenItFails: string) => Promise<void>;

/** Which dev fields this person works in — and therefore which work they see. */
function FieldAccess({
  member,
  fields,
  onPatch,
}: {
  member: RosterMember;
  fields: FieldSummary[];
  onPatch: PatchFn;
}) {
  function toggle(fieldId: string): void {
    const next = member.fieldIds.includes(fieldId)
      ? member.fieldIds.filter((id) => id !== fieldId)
      : [...member.fieldIds, fieldId];
    void onPatch({ fieldIds: next }, 'Could not change their field access');
  }

  return (
    <fieldset className="flex flex-col gap-1.5">
      <legend className="text-xs font-medium text-ink-soft">Dev fields</legend>
      <p className="text-[11px] text-ink-faint">
        A member only ever sees tasks in the fields they are in. This is the access boundary, not a
        label.
      </p>
      <div className="mt-1 flex flex-wrap gap-2">
        {fields.map((field) => {
          const on = member.fieldIds.includes(field.id);
          return (
            <button
              key={field.id}
              type="button"
              onClick={() => toggle(field.id)}
              aria-pressed={on}
              className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition-colors ${
                on ? 'border-accent bg-accent/15 text-ink' : 'border-line text-ink-soft hover:bg-hover'
              }`}
            >
              <span className="size-1.5 rounded-full" style={{ backgroundColor: field.color }} />
              {field.name}
            </button>
          );
        })}
        {fields.length === 0 && (
          <span className="text-[11px] text-ink-faint">
            No dev fields exist yet — create one in the Fields tab first.
          </span>
        )}
      </div>
    </fieldset>
  );
}

/** Which pages exist for this person at all. */
function SectionAccess({ member, onPatch }: { member: RosterMember; onPatch: PatchFn }) {
  function toggle(section: Section): void {
    const current = member.access.sections;
    const next = current.includes(section)
      ? current.filter((value) => value !== section)
      : SECTIONS.filter((value) => value === section || current.includes(value));
    void onPatch(
      { permissions: { sections: next, abilities: member.access.abilities } },
      'Could not change what they can see',
    );
  }

  return (
    <fieldset className="flex flex-col gap-1.5">
      <legend className="text-xs font-medium text-ink-soft">Pages they can open</legend>
      <div className="mt-1 flex flex-col gap-1.5">
        {SECTIONS.map((section) => (
          <Toggle
            key={section}
            on={member.access.sections.includes(section)}
            title={SECTION_LABELS[section].title}
            hint={SECTION_LABELS[section].hint}
            onChange={() => toggle(section)}
          />
        ))}
      </div>
      {member.access.sections.length === 0 && (
        <p className="text-[11px] text-blocked">
          With no page open to them, this person can sign in and reach nothing.
        </p>
      )}
    </fieldset>
  );
}

/** What they may do with the work they can see. */
function AbilityAccess({ member, onPatch }: { member: RosterMember; onPatch: PatchFn }) {
  function toggle(ability: Ability): void {
    const current = member.access.abilities as Ability[];
    const next = current.includes(ability)
      ? current.filter((value) => value !== ability)
      : ABILITIES.filter((value) => value === ability || current.includes(value));
    void onPatch(
      { permissions: { sections: member.access.sections, abilities: next } },
      'Could not change what they can do',
    );
  }

  return (
    <fieldset className="flex flex-col gap-1.5">
      <legend className="text-xs font-medium text-ink-soft">What they can do</legend>
      <div className="mt-1 flex flex-col gap-1.5">
        {ABILITIES.map((ability) => (
          <Toggle
            key={ability}
            on={member.access.abilities.includes(ability)}
            title={ABILITY_LABELS[ability].title}
            hint={ABILITY_LABELS[ability].hint}
            onChange={() => toggle(ability)}
          />
        ))}
      </div>
    </fieldset>
  );
}

/**
 * The view this person starts on. An admin sets it; the member rearranges it
 * afterwards from the board's own View menu — so once they have, this panel says
 * so and offers to put them back on the team's version rather than silently
 * overwriting what they chose.
 */
function StartingLayout({
  member,
  fields,
  onPatch,
}: {
  member: RosterMember;
  fields: FieldSummary[];
  onPatch: PatchFn;
}) {
  const layout = member.layoutDefaults;
  const pages = SECTIONS.filter((section) => member.access.sections.includes(section));

  function save(patch: Partial<MemberLayout>): void {
    void onPatch({ layoutDefaults: patch }, 'Could not change their starting view');
  }

  function toggleColumn(status: (typeof TASK_STATUSES)[number]): void {
    const on = layout.boardColumns.includes(status);
    const next = on
      ? layout.boardColumns.filter((value) => value !== status)
      : TASK_STATUSES.filter((value) => value === status || layout.boardColumns.includes(value));
    if (next.length === 0) return;
    save({ boardColumns: next });
  }

  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="text-xs font-medium text-ink-soft">The view they start on</legend>
      <p className="text-[11px] text-ink-faint">
        What this person sees the first time they open the team. They can rearrange it for
        themselves afterwards — this stays the version you hand them.
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        {pages.length > 0 && (
          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] text-ink-soft">Opens on</span>
            <select
              value={pages.includes(layout.landing) ? layout.landing : pages[0]}
              onChange={(event) => save({ landing: event.target.value as Section })}
              className="field-input text-sm"
            >
              {pages.map((section) => (
                <option key={section} value={section}>
                  {SECTION_LABELS[section].title}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] text-ink-soft">Board shows</span>
          <select
            value={layout.boardField}
            onChange={(event) => save({ boardField: event.target.value })}
            className="field-input text-sm"
          >
            <option value="all">All their fields</option>
            {fields
              .filter((field) => member.fieldIds.includes(field.id))
              .map((field) => (
                <option key={field.id} value={field.id}>
                  Just {field.name}
                </option>
              ))}
          </select>
        </label>
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="text-[11px] text-ink-soft">Columns</span>
        <div className="flex flex-wrap gap-1.5">
          {TASK_STATUSES.map((status) => {
            const on = layout.boardColumns.includes(status);
            return (
              <button
                key={status}
                type="button"
                onClick={() => toggleColumn(status)}
                aria-pressed={on}
                className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
                  on ? 'border-accent bg-accent/15 text-ink' : 'border-line text-ink-soft hover:bg-hover'
                }`}
              >
                {COLUMN_LABELS[status]}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] text-ink-soft">Card size</span>
        {(['comfortable', 'compact'] as Density[]).map((density) => (
          <button
            key={density}
            type="button"
            onClick={() => save({ density })}
            aria-pressed={layout.density === density}
            className={`rounded-full border px-2.5 py-1 text-[11px] capitalize transition-colors ${
              layout.density === density
                ? 'border-accent bg-accent/15 text-ink'
                : 'border-line text-ink-soft hover:bg-hover'
            }`}
          >
            {density}
          </button>
        ))}
      </div>

      {member.hasOwnLayout && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-line bg-canvas px-3 py-2">
          <span className="text-[11px] text-ink-faint">
            {member.displayName.split(' ')[0]} has since arranged their own view, so changes here no
            longer reach them.
          </span>
          <button
            type="button"
            onClick={() =>
              void onPatch({ resetLayout: true }, 'Could not reset their view')
            }
            className="btn btn-ghost !py-1 !text-xs"
          >
            Put them back on this one
          </button>
        </div>
      )}
    </fieldset>
  );
}

/** One permission: a switch, its name, and what turning it on actually allows. */
function Toggle({
  on,
  title,
  hint,
  onChange,
}: {
  on: boolean;
  title: string;
  hint: string;
  onChange: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onChange}
      aria-pressed={on}
      className="flex cursor-pointer items-start gap-2.5 rounded-lg px-1 py-1 text-left transition-colors hover:bg-hover"
    >
      <span
        aria-hidden="true"
        className={`mt-0.5 grid size-4 shrink-0 place-items-center rounded border transition-colors ${
          on ? 'border-accent bg-accent text-canvas' : 'border-line-strong text-transparent'
        }`}
      >
        <svg viewBox="0 0 16 16" className="size-3" fill="currentColor">
          <path d="M13.5 4.2a.9.9 0 0 1 0 1.28l-6.1 6.1a.9.9 0 0 1-1.28 0L2.9 8.36a.9.9 0 0 1 1.28-1.27l2.58 2.58 5.46-5.47a.9.9 0 0 1 1.28 0Z" />
        </svg>
      </span>
      <span className="min-w-0">
        <span className="block text-xs font-medium">{title}</span>
        <span className="block text-[11px] leading-snug text-ink-faint">{hint}</span>
      </span>
    </button>
  );
}
